import { Link } from 'react-router'
import { Briefcase, Building2, CalendarDays, Mail, MapPin, Phone, UserRound } from 'lucide-react'
import { DateValue, StatusBadge } from '@/components/common'
import { Card } from '@/components/ui/card'
import type { AssignmentView, EmployeeDetail } from '../types'
import { serviceLength } from './serviceLength'

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts.at(-1)![0] : '')).toUpperCase() || '?'
}

function humanise(value: string | null | undefined): string | null {
  return value ? value.toLowerCase().replace(/_/g, ' ') : null
}

function QuickFact({
  icon: Icon, label, children,
}: { icon: typeof Mail; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 items-start gap-2">
      <Icon className="mt-0.5 size-4 shrink-0 text-[var(--color-muted)]" aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-xs text-[var(--color-muted)]">{label}</p>
        <p className="truncate text-sm text-[var(--color-heading)]">{children ?? '—'}</p>
      </div>
    </div>
  )
}

/**
 * The identity strip at the top of a profile: who this is, what they do, and how to reach them.
 *
 * <p>The job line reads from the assignment in force today rather than the employee summary,
 * for the same reason the Overview does: a move recorded for next month is already on file.
 */
export function ProfileHeaderCard({
  employee, current,
}: {
  employee: EmployeeDetail
  current: AssignmentView | undefined
}) {
  const { summary } = employee
  const service = serviceLength(summary.joinDate)
  const designation = current?.designationName ?? summary.designationName
  const department = current?.departmentName ?? summary.departmentName

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
        <div className="flex min-w-0 items-center gap-4 lg:w-[22rem] lg:shrink-0">
          <span
            className="grid size-16 shrink-0 place-items-center rounded-full bg-[var(--color-primary-soft)] text-xl font-semibold text-[var(--color-primary)]"
            aria-hidden="true"
          >
            {initials(summary.displayName)}
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-[var(--color-heading)]">
              {summary.displayName}
            </h2>
            <p className="truncate text-sm text-[var(--color-body)]">
              {designation ?? 'No designation on file'}
              {department ? ` · ${department}` : ''}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge
                status={summary.status}
                tone={summary.status === 'ACTIVE' ? 'success' : 'neutral'}
                label={humanise(summary.status) ?? summary.status}
              />
              <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs capitalize text-[var(--color-body)]">
                {humanise(summary.employmentType)}
              </span>
              <span className="text-xs text-[var(--color-muted)]">{summary.employeeCode}</span>
            </div>
          </div>
        </div>

        <dl className="grid flex-1 grid-cols-1 gap-4 border-t border-[var(--color-border)] pt-4 sm:grid-cols-2 lg:grid-cols-3 lg:border-s lg:border-t-0 lg:ps-5 lg:pt-0">
          <QuickFact icon={CalendarDays} label="Joined">
            {summary.joinDate ? <DateValue value={summary.joinDate} /> : null}
          </QuickFact>
          <QuickFact icon={Briefcase} label="Length of service">{service?.label}</QuickFact>
          <QuickFact icon={UserRound} label="Reports to">
            {current?.managerEmployeeId
              ? (
                <Link
                  to={`/employees/${current.managerEmployeeId}`}
                  className="text-[var(--color-primary)] hover:underline"
                >
                  {current.managerName ?? 'Manager'}
                </Link>
              )
              : summary.managerName}
          </QuickFact>
          <QuickFact icon={Building2} label="Grade">{current?.gradeName ?? summary.gradeName}</QuickFact>
          <QuickFact icon={MapPin} label="Location">{current?.locationName ?? summary.locationName}</QuickFact>
          <QuickFact icon={Mail} label="Work email">{summary.workEmail}</QuickFact>
          <QuickFact icon={Phone} label="Work phone">{summary.workPhone}</QuickFact>
        </dl>
      </div>
    </Card>
  )
}
